import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    IconButton,
    Box,
    TextField,
    CircularProgress,
    MenuItem,
    Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CloseIcon from '@mui/icons-material/Close';
import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';
import config from 'src/config';

const { PRIVATE_ROUTE } = API_ROUTES;

const SubmitDwrModal = ({
    open,
    onClose,
    selectedWork,
    taskNumber,
    jobNumber,
    dwrNumber,
    costItem: parentCostItem,
    onDwrSubmitted,
}) => {
    const { authAxios } = useContext(FetchContext);

    const [dwrHours, setDwrHours] = useState('1');
    const [remark, setRemark] = useState('');
    const [showWarning, setShowWarning] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [taskDate, setTaskDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [fieldRequestedDate, setFieldRequestedDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [selectedCostItem, setSelectedCostItem] = useState('');
    const [loadingCostItems, setLoadingCostItems] = useState(false);
    const [draftCostItems, setDraftCostItems] = useState([]);

    /*
* Logged-in employee
*/
    const user_data = JSON.parse(
        localStorage.getItem(config.PROJECT_NAME)
    );

    const loggedInUserId = user_data?.user?._id;

    const userRole = user_data?.user?.role;

    const isManager = ['manager', 'project_manager'].includes(
        String(userRole || '').toLowerCase());



    const employeeName =
        `${user_data?.user?.first_name || ''} ${user_data?.user?.last_name || ''
            }`.trim();

    useEffect(() => {
        if (open) {

            const initialTaskDate =
                selectedWork?.type === 'todoDraft'
                    ? dayjs().format('YYYY-MM-DD')
                    : selectedWork?.schedule?.planned_date
                        ? String(selectedWork.schedule.planned_date).substring(0, 10)
                        : dayjs().format('YYYY-MM-DD');

            const initialDwrHours =
                selectedWork?.schedule?.task?.billing_line_items
                    ?.labour_item?.labour_cost_items?.[0]?.hours;

            const initialRemark =
                selectedWork?.schedule?.remark;

            const todoDraft = selectedWork?.todoDraft || {};

            const initialFieldRequestedDate =
                selectedWork?.type === 'todoDraft'
                    ? (
                        todoDraft.field_trip_date ||
                        dayjs().format('YYYY-MM-DD')
                    )
                    : initialTaskDate;

            setFieldRequestedDate(
                String(initialFieldRequestedDate).substring(0, 10)
            );

            setTaskDate(
                initialTaskDate

            );

            setDwrHours(
                initialDwrHours !== null &&
                    initialDwrHours !== undefined &&
                    initialDwrHours !== ''
                    ? String(initialDwrHours)
                    : '1'
            );

            setRemark(
                initialRemark !== null &&
                    initialRemark !== undefined
                    ? initialRemark
                    : ''
            );

            setShowWarning(false);
            setShowSuccess(false);
            setSubmitting(false);
        }
    }, [open, selectedWork]);

    useEffect(() => {
        const getCostItems = async () => {
            if (!open || !isManager) return;

            try {
                setLoadingCostItems(true);

                const response = await authAxios.get(
                    PRIVATE_ROUTE.GET_DRAFT_COST_ITEMS
                );

                const items = response.data?.data || [];

                setDraftCostItems(items);

                const existingCostItem =
                    parentCostItem ||
                    selectedWork?.todoDraft?.costitem ||
                    (Array.isArray(selectedWork?.todoDraft?.cost_item)
                        ? selectedWork.todoDraft.cost_item[0]
                        : selectedWork?.todoDraft?.cost_item);

                setSelectedCostItem(
                    items.includes(existingCostItem)
                        ? existingCostItem
                        : items[0] || ''
                );
            } catch (err) {
                console.error('Error fetching draft cost items:', err);
                toast.error('Unable to load cost items.');
            } finally {
                setLoadingCostItems(false);
            }
        };

        getCostItems();
    }, [
        open,
        isManager,
        authAxios,
        PRIVATE_ROUTE.GET_DRAFT_COST_ITEMS,
        parentCostItem,
        selectedWork?.todoDraft?.costitem,
    ]);

    if (!selectedWork) {
        return null;
    }

    const isTodoDraft = selectedWork?.type === 'todoDraft';

    const schedule = selectedWork?.schedule || {};
    const todoDraft = selectedWork?.todoDraft || {};
    const isEdit = Boolean(dwrNumber);

    /*
     * Use the schedule for schedule work
     * and the todo draft for todo draft work.
     */
    const workItem = isTodoDraft ? todoDraft : schedule;

    // ============================================================
    // SOURCE OF TRUTH
    //
    // Schedule -> Schedule fields
    // TodoDraft -> TodoDraft fields
    //
    // Task is NOT used as a fallback for DWR business data.
    // ============================================================

    const description = workItem?.description || '';

    // ============================================================
    // TASK / JOB DISPLAY NUMBERS
    //
    // findAvailableWork now returns:
    //
    //   task_number
    //   job_number
    //
    // Use the selected work item directly.
    //
    // TodoDraft:
    //   todoDraft.task_number
    //   todoDraft.job_number
    //
    // Schedule:
    //   schedule.task_number
    //   schedule.job_number
    // ============================================================

    const displayTaskNumber =
        workItem?.task_number ||
        taskNumber ||
        '';

    const displayJobNumber =
        workItem?.job_number ||
        jobNumber ||
        '';

    /*
     * Cost Item
     */
    const scheduleCostItem = Array.isArray(
        schedule?.cost_item
    )
        ? schedule.cost_item
        : schedule?.cost_item
            ? [schedule.cost_item]
            : [];

    const todoDraftCostItem = Array.isArray(
        todoDraft?.cost_item
    )
        ? todoDraft.cost_item
        : todoDraft?.cost_item
            ? [todoDraft.cost_item]
            : [];

    /*
     * Cost Items - Schedule / TodoDraft
     */
    /*
     * Cost Items - Schedule / TodoDraft
     */
    /*
     * Cost Item
     * Managers: use the selected API cost item.
     * Non-managers: retain the existing work-item cost item.
     */

    const rawWorkCostItems = isTodoDraft
        ? todoDraft?.cost_item
        : schedule?.cost_item;

    const workCostItems = (
        Array.isArray(rawWorkCostItems)
            ? rawWorkCostItems
            : rawWorkCostItems
                ? [rawWorkCostItems]
                : []
    ).map((item) => {
        if (typeof item === 'string') {
            return item;
        }

        if (typeof item === 'object' && item !== null) {
            return (
                item.cost_item ||
                item.costitem ||
                item.name ||
                item.label ||
                item.number_str ||
                item.value ||
                ''
            );
        }

        return String(item);
    }).filter(Boolean);

    const defaultCostItem = workCostItems.join(', ') || '-';

    const costItem = isManager
        ? selectedCostItem
        : (
            parentCostItem ||
            defaultCostItem
        );

    /*
     * Submit Date
     */
    const submitDate = dayjs().format('YYYY-MM-DD');

    /*
     * Assigned member
     *
     * Keep the existing DWR lookup behavior.
     */
    const assignedMembers =
        workItem?.assigned_members ||
        schedule?.assigned_members ||
        [];

    const assignedMember = assignedMembers.find((member) => {
        const employeeId =
            member?.employee?._id ||
            member?.employee ||
            member?._id;

        return String(employeeId) === String(loggedInUserId);
    });

    const linkedDwrId = assignedMember?.dwr_id || null;
    const linkedDwrStatus =
        assignedMember?.dwr_status || null;

    /*
     * DWR Hours
     *
     * Maximum 1 decimal place.
     */
    const handleHoursChange = (event) => {
        const value = event.target.value;

        if (value === '') {
            setDwrHours('');
            return;
        }

        if (/^\d*\.?\d{0,2}$/.test(value)) {
            setDwrHours(value);
        }
    };

    /*
     * Open confirmation
     */
    const handleSubmitClick = () => {
        if (!dwrHours || Number(dwrHours) <= 0) {
            return;
        }

        setShowWarning(true);
    };

    /*
     * Final API submission
     */
    const handleConfirmSubmit = async () => {
        if (submitting) {
            return;
        }

        try {
            setSubmitting(true);

            /*
             * EDIT EXISTING DWR
             *
             * Only send fields that are editable from SubmitDwrModal.
             */
            if (isEdit) {
                const editData = {
                    task_date: taskDate,
                    task_hour: Number(dwrHours),
                    remark: remark || '',
                    description,
                    submit_date: new Date(),
                };

                const response = await authAxios.post(
                    `${PRIVATE_ROUTE.STAFF_UPDATE_DWR}${dwrNumber}`,
                    editData
                );

                if (response.data?.statusCode === 200) {
                    toast.success('DWR updated successfully.');

                    onDwrSubmitted?.({
                        dwrId:
                            response.data?.data?._id ||
                            response.data?.dwr?._id ||
                            response.data?.data?.dwr_id ||
                            dwrNumber,
                    });

                    setShowWarning(false);
                    setDwrHours('1');
                    setRemark('');
                    onClose();
                } else {
                    toast.error(
                        response.data?.message ||
                        response.data?.massage ||
                        'Failed to update DWR.'
                    );
                }

                return;
            }

            /*
             * CREATE / RESUBMIT DWR
             *
             * Everything below this point applies only to a new submission.
             */

            if (!workItem?._id && !schedule?._id) {
                toast.error('Work information is missing.');
                return;
            }

            if (!loggedInUserId) {
                toast.error('Employee information is missing.');
                return;
            }

            /*
             * Pending DWR cannot be submitted again.
             */
            if (linkedDwrStatus === 'pending') {
                toast.error('This DWR has already been submitted.');
                return;
            }

            /*
             * Approved DWR cannot be submitted again.
             */
            if (linkedDwrStatus === 'approved') {
                toast.error('This DWR has already been approved.');
                return;
            }

            const scheduleId = !isTodoDraft
                ? schedule?._id || null
                : null;

            const todoDraftId = isTodoDraft
                ? todoDraft?._id || null
                : null;

            const taskId =
                schedule?.task?._id ||
                schedule?.task_id?._id ||
                schedule?.task_id ||
                todoDraft?.task_id?._id ||
                todoDraft?.task_id ||
                null;

            if (!taskId) {
                toast.error('Task information is missing.');
                return;
            }

            if (isManager && !selectedCostItem) {
                toast.error('Please select a cost item.');
                return;
            }

            const newdata = {
                schedule_id: scheduleId,
                todo_draft_id: todoDraftId,
                task_id: taskId,
                user_id: loggedInUserId,
                dwr_id: linkedDwrId,
                task_name: displayTaskNumber,
                task_date: taskDate,
                task_hour: Number(dwrHours),
                status: 0,
                submit_status: true,
                submit_date: new Date(),

                labourCosts: [
                    {
                        employee: loggedInUserId,
                        employee_name: employeeName,
                        costitem: costItem,
                        hours: Number(dwrHours),
                    },
                ],

                equipment: [],
                remark: remark || '',
                description,
            };

            const response = await authAxios.post(
                PRIVATE_ROUTE.CREATE_DWR,
                newdata
            );

            if (response.data?.statusCode === 200) {
                toast.success(
                    linkedDwrId
                        ? 'DWR resubmitted successfully.'
                        : 'DWR submitted successfully.'
                );

                onDwrSubmitted?.({
                    scheduleId,
                    todoDraftId,
                    dwrId:
                        response.data?.data?._id ||
                        response.data?.dwr?._id ||
                        response.data?.data?.dwr_id ||
                        linkedDwrId,
                });

                setShowWarning(false);
                setDwrHours('1');
                setRemark('');
                onClose();
            } else {
                toast.error(
                    response.data?.message ||
                    response.data?.massage ||
                    'Failed to submit DWR.'
                );
            }
        } catch (error) {
            console.error('Submit DWR error:', error);

            toast.error(
                error?.response?.data?.message ||
                error?.response?.data?.massage ||
                (isEdit
                    ? 'Failed to update DWR. Please try again.'
                    : 'Failed to submit DWR. Please try again.')
            );
        } finally {
            setSubmitting(false);
        }
    };
    /*
     * Cancel
     */
    const handleCancel = () => {
        if (submitting) {
            return;
        }

        setShowWarning(false);
        onClose();
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog
                open={open}
                onClose={handleCancel}
                fullWidth
                maxWidth="sm"
                fullScreen={false}
                PaperProps={{
                    sx: {
                        borderRadius: { xs: 0, sm: 2 },
                        maxHeight: { xs: '100vh', sm: '90vh' },
                        width: '100%',
                        m: { xs: 0, sm: 2 },
                    },
                }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        bgcolor: '#65C8D0',
                        color: '#fff',
                        py: { xs: 1.25, sm: 1.5 },
                        px: { xs: 2, sm: 3 },
                        gap: 1,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{
                                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                            }}
                        >
                            {isEdit ? 'Edit DWR' : 'Submit DWR'}
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{
                                mt: 0.25,
                                fontWeight: 500,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                wordBreak: 'break-word',
                            }}
                        >
                            Cost Item # {costItem || '-'}
                        </Typography>

                        <Typography
                            sx={{
                                color:
                                    'rgba(255,255,255,0.9)',
                                fontSize: '0.78rem',
                                mt: 0.25,
                            }}
                        >
                            Job{' '}
                            {displayJobNumber
                                || '-'}
                            {' • '}
                            Task{' '}
                            {displayTaskNumber
                                || '-'}
                        </Typography>

                    </Box>

                    <IconButton
                        onClick={handleCancel}
                        disabled={submitting}
                        sx={{
                            color: '#fff',
                            flexShrink: 0,
                            p: 0.75,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                {/* Body */}
                <DialogContent
                    sx={{
                        p: { xs: 1.5, sm: 2 },
                        overflowY: 'auto',
                    }}
                >
                    {!showWarning ? (
                        <Box>
                            {/* Field Requested Date */}
                            <TextField
                                fullWidth
                                label="Field/Requested Date"
                                value={
                                    fieldRequestedDate
                                        ? dayjs(fieldRequestedDate).format('MM/DD/YYYY')
                                        : null}
                                margin="normal"
                                disabled
                                inputProps={{
                                    inputMode: 'decimal',
                                    step: 0.01,
                                    min: 0,
                                }}
                            />
                            {/* Task Date */}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Task Date"
                                    value={taskDate ? dayjs(taskDate) : null}

                                    onChange={(newValue) => {
                                        if (newValue && newValue.isValid()) {
                                            setTaskDate(newValue.format('YYYY-MM-DD'));
                                        }
                                    }}
                                    disableFuture
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            margin="normal"
                                        />
                                    )}
                                />
                            </LocalizationProvider>

                            {/* Cost Item - Manager only */}
                            {isManager && (
                                <TextField
                                    select
                                    fullWidth
                                    label="Cost Item"
                                    value={selectedCostItem}
                                    onChange={(event) => setSelectedCostItem(event.target.value)}
                                    margin="normal"
                                    disabled={loadingCostItems || draftCostItems.length === 0}
                                    helperText={
                                        loadingCostItems
                                            ? 'Loading cost items...'
                                            : draftCostItems.length === 0
                                                ? 'No cost items available.'
                                                : ''
                                    }
                                >
                                    {draftCostItems.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}

                            {/* DWR Hours */}
                            <TextField
                                fullWidth
                                label="DWR Hour"
                                value={dwrHours}
                                onChange={handleHoursChange}
                                margin="normal"
                                inputProps={{
                                    inputMode: 'decimal',
                                    step: 0.01,
                                    min: 0,
                                }}
                                helperText="Maximum 2 decimal place, e.g. 1.50"
                            />

                            {/* Remark */}
                            <TextField
                                fullWidth
                                label="Remark"
                                value={remark}
                                onChange={(event) => {
                                    setRemark(event.target.value);
                                }}
                                margin="normal"
                                multiline
                                minRows={3}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ py: 1 }}>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ mb: 2 }}
                            >
                                {isEdit ? 'Confirm DWR Update' : 'Confirm DWR Submission'}
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{ mb: 2 }}
                            >
                                Please review the DWR details before{' '}
                                {isEdit ? 'updating' : 'submitting'}.
                            </Typography>

                            {/* Summary */}
                            <Box
                                sx={{
                                    p: { xs: 1.25, sm: 1.5 },
                                    backgroundColor: '#f8fafb',
                                    borderRadius: 1.5,
                                    border: '1px solid #e6eaed',
                                }}
                            >
                                {/* Cost Item */}
                                <Box
                                    sx={{
                                        py: 0.75,
                                        borderBottom: '1px solid #e6eaed',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            lineHeight: 1.25,
                                            mb: 0.3,
                                        }}
                                    >
                                        Cost Item
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                            fontWeight: 600,
                                            lineHeight: 1.4,
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {costItem}
                                    </Typography>
                                </Box>

                                {/* Employee */}
                                <Box
                                    sx={{
                                        py: 0.75,
                                        borderBottom: '1px solid #e6eaed',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            lineHeight: 1.25,
                                            mb: 0.3,
                                        }}
                                    >
                                        Employee
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                            fontWeight: 600,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {employeeName || '-'}
                                    </Typography>
                                </Box>

                                {/* Task Date */}
                                <Box
                                    sx={{
                                        py: 0.75,
                                        borderBottom: '1px solid #e6eaed',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            lineHeight: 1.25,
                                            mb: 0.3,
                                        }}
                                    >
                                        Task Date
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                            fontWeight: 600,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {taskDate}
                                    </Typography>
                                </Box>

                                {/* Submit Date */}
                                <Box
                                    sx={{
                                        py: 0.75,
                                        borderBottom: '1px solid #e6eaed',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            lineHeight: 1.25,
                                            mb: 0.3,
                                        }}
                                    >
                                        Submit Date
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                            fontWeight: 600,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {submitDate}
                                    </Typography>
                                </Box>

                                {/* DWR Hour */}
                                <Box
                                    sx={{
                                        py: 0.75,
                                        borderBottom: '1px solid #e6eaed',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            lineHeight: 1.25,
                                            mb: 0.3,
                                        }}
                                    >
                                        DWR Hour
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                            fontWeight: 600,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {dwrHours || '-'}
                                    </Typography>
                                </Box>

                                {/* Remark */}
                                <Box
                                    sx={{
                                        pt: 0.75,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            lineHeight: 1.25,
                                            mb: 0.3,
                                        }}
                                    >
                                        Remark
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                            fontWeight: 600,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {remark || '-'}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Warning */}
                            <Box
                                sx={{
                                    mt: 2,
                                    p: { xs: 1.5, sm: 2 },
                                    backgroundColor: '#fff3f3',
                                    borderRadius: 1,
                                    border: '1px solid #ffcdd2',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{
                                        fontWeight: 600,
                                    }}
                                >
                                    Once submitted, the DWR will
                                    be sent to the manager for
                                    review.
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                {/* Footer */}
                {/* Footer */}
                <DialogActions
                    sx={{
                        px: { xs: 1.5, sm: 3 },
                        pb: { xs: 1.5, sm: 2 },
                        pt: 1,
                        width: '100%',
                        boxSizing: 'border-box',
                        display: 'block',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1,
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        {!showWarning ? (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancel}
                                    disabled={submitting}
                                    sx={{
                                        minHeight: 44,
                                        width: '100%',
                                        minWidth: 0,
                                        flex: { sm: 1 },
                                        boxSizing: 'border-box',
                                        m: 0,
                                    }}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={handleSubmitClick}
                                    disabled={
                                        submitting ||
                                        !dwrHours ||
                                        Number(dwrHours) <= 0
                                    }
                                    sx={{
                                        minHeight: 44,
                                        width: '100%',
                                        minWidth: 0,
                                        flex: { sm: 1 },
                                        boxSizing: 'border-box',
                                        m: 0,
                                        backgroundColor: '#3DADB5',
                                        '&:hover': {
                                            backgroundColor: '#2E8F96',
                                        },
                                    }}
                                >
                                    Confirm
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowWarning(false)}
                                    disabled={submitting}
                                    sx={{
                                        minHeight: 44,
                                        width: '100%',
                                        minWidth: 0,
                                        flex: { sm: 1 },
                                        boxSizing: 'border-box',
                                        m: 0,
                                    }}
                                >
                                    Back
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={handleConfirmSubmit}
                                    disabled={submitting}
                                    startIcon={
                                        submitting ? (
                                            <CircularProgress
                                                size={18}
                                                color="inherit"
                                            />
                                        ) : null
                                    }
                                    sx={{
                                        minHeight: 44,
                                        width: '100%',
                                        minWidth: 0,
                                        flex: { sm: 1 },
                                        boxSizing: 'border-box',
                                        m: 0,
                                        backgroundColor: '#3DADB5',
                                        color: '#fff',
                                        fontWeight: 600,
                                        '&:hover': {
                                            backgroundColor: '#2E8F96',
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: '#b7dfe2',
                                            color: '#fff',
                                        },
                                    }}
                                >
                                    {submitting
                                        ? isEdit
                                            ? 'Updating...'
                                            : 'Submitting...'
                                        : isEdit
                                            ? 'Update DWR'
                                            : 'Submit DWR'}
                                </Button>
                            </>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

SubmitDwrModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    selectedWork: PropTypes.object,
    taskNumber: PropTypes.string,
    jobNumber: PropTypes.string,
    dwrNumber: PropTypes.string,
    costItem: PropTypes.string,
    onDwrSubmitted: PropTypes.func,
};

SubmitDwrModal.defaultProps = {
    open: false,
    onClose: () => { },
    selectedWork: null,
    taskNumber: '',
    dwrNumber: '',
    costItem: '',
    onDwrSubmitted: () => { },
};

export default SubmitDwrModal;